import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

type ChartData = {
  month: string; 
  percentage: number | null;
  __fake?: boolean;
};

type ComplianceChartProps = {
  data: ChartData[];
};

export function ComplianceChart({ data }: ComplianceChartProps) {
  const monthMap: Record<string, string> = {
    Jan: "January",
    Feb: "February",
    Mar: "March",
    Apr: "April",
    May: "May",
    Jun: "June",
    Jul: "July",
    Aug: "August",
    Sep: "September",
    Oct: "October",
    Nov: "November",
    Dec: "December",
  };

  const formatTooltipLabel = (label: string) => {
    const match = String(label).match(/^(\w+)\s+'(\d{2})$/);
    if (match) {
      const monthAbbr = match[1];
      const year = `20${match[2]}`;
      const fullMonth = monthMap[monthAbbr] || monthAbbr;
      return `${fullMonth} ${year}`;
    }
    return label;
  };


  const finalData: ChartData[] =
    data && data.length === 1
      ? [
          { month: data[0].month, percentage: data[0].percentage },
          {
            month: `${data[0].month}__anchor`,
            percentage: null,
            __fake: true,
          },
        ]
      : data;

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={finalData}
          margin={{ top: 10, right: 10, left: -10, bottom: 10 }}
        >
          <defs>
            <linearGradient id="colorPercentage" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.1} />
              <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />

          <XAxis
            dataKey="month"
            tick={{ fill: "#6b7280", fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: "#d1d5db" }}
            dy={8}
            angle={-45}
            textAnchor="end"
            height={60}
            interval={0}
            minTickGap={0}
            allowDataOverflow={false}
            tickFormatter={(value: any, index: number) => {
              const payload = finalData?.[index];
              if (payload?.__fake) return ""; 
              return value;
            }}
          />

          <YAxis
            tick={{ fill: "#6b7280", fontSize: 13 }}
            tickLine={false}
            axisLine={{ stroke: "#d1d5db" }}
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={(v) => `${v}%`}
            label={{
              value: "% Complete",
              angle: -90,
              position: "insideLeft",
              style: { fill: "#6b7280", fontSize: 13, textAnchor: "middle" },
            }}
          />

          <Tooltip
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              boxShadow:
                "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
              padding: "12px",
            }}
            labelStyle={{ color: "#374151", fontWeight: 600, marginBottom: "4px" }}
            itemStyle={{ color: "#2563eb", fontWeight: 500 }}
            labelFormatter={(label: any, payload: any[]) => {
              if (payload?.[0]?.payload?.__fake) return "";
              return formatTooltipLabel(label);
            }}
            formatter={(value: any, _name: any, props: any) => {
              if (props?.payload?.__fake) return null;
              return [`${value}%`, "Completion"];
            }}
          />

          <Line
            type="monotone"
            dataKey="percentage"
            name="Compliance"
            stroke="#2563eb"
            strokeWidth={3}
            dot={(dotProps: any) => {
              if (dotProps?.payload?.__fake) {
                return <g />;
              }
              return (
                <circle
                  cx={dotProps.cx}
                  cy={dotProps.cy}
                  r={5}
                  fill="#2563eb"
                  stroke="#fff"
                  strokeWidth={2}
                />
              );
            }}
            activeDot={(dotProps: any) => {
              if (dotProps?.payload?.__fake) return <g />;
              return (
                <circle
                  cx={dotProps.cx}
                  cy={dotProps.cy}
                  r={7}
                  fill="#2563eb"
                  stroke="#fff"
                  strokeWidth={2}
                />
              );
            }}
            fill="url(#colorPercentage)"
            isAnimationActive={false} 
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
